import { HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TdDialogService } from '@covalent/core/dialogs';
import { Store } from '@ngrx/store';
import { truncate } from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs';
import { finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { MaintenanceHttpClient } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.service';
import { AppState } from 'src/store';
import { selectFlightLogById } from 'src/store/flight-log/flight-log.selectors';
import { filterNull } from 'src/util/rxjs/operators';
import { isNullOrEmpty } from 'src/util/string';
import { LogStatus } from '../../flight-log-api/flight-logs/flight-log-client.model';

const maxMxImageFileSizeInBytes = 10485760;

type OperationMethod = 'cancel' | 'retrieve' | 'remove' | 'none' | 'upload' | null;

interface MxImageOperation {
  running$: BehaviorSubject<boolean>;
  progress$: BehaviorSubject<number>;
  name$: BehaviorSubject<string | undefined>;
  sub: Subscription;
  readonly maxNameLength: number;
  method: OperationMethod;
}

@Component({
  selector: 'fl-maintenance-image-control',
  templateUrl: './maintenance-image-control.component.html',
  styleUrls: ['./maintenance-image-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceImageControlComponent implements OnInit, OnDestroy {
  @Input()
  public flightLogId: string;

  public mxOperation: MxImageOperation = {
    method: null,
    running$: new BehaviorSubject<boolean>(false),
    progress$: new BehaviorSubject<number>(0),
    name$: new BehaviorSubject<string | undefined>(undefined),
    maxNameLength: 100,
    sub: Subscription.EMPTY,
  };

  public _onDestroy$ = new Subject();
  public flightLogNumber$: Observable<string | undefined>;
  public flightLogStatus$: Observable<LogStatus | undefined>;
  public flightLogCompleted$ = new BehaviorSubject<boolean>(false);

  get isFlightLogComplete$(): Observable<boolean> {
    return this.flightLogCompleted$.asObservable();
  }

  get isMxImageRemoving$(): Observable<boolean> {
    return this.isMaintenceImageOperationInProgress$('remove');
  }

  get isMxImageRetrieving$(): Observable<boolean> {
    return this.isMaintenceImageOperationInProgress$('retrieve');
  }

  get isMxImageProcessing$(): Observable<boolean> {
    return this.isMaintenceImageOperationInProgress$('process');
  }

  get isMxImageUploading$(): Observable<boolean> {
    return this.isMaintenceImageOperationInProgress$('upload');
  }

  get mxImageName$(): Observable<string | undefined> {
    return this.mxOperation.name$.asObservable();
  }

  get mxImageProgress$(): Observable<number> {
    return this.mxOperation.progress$.asObservable();
  }

  get mxImageUploadDisplayName$(): Observable<string> {
    return this.mxImageName$.pipe(
      map((imgName) => {
        return `${isNullOrEmpty(imgName) ? 'Upload' : 'Replace'} Image`;
      }),
    );
  }

  @ViewChild('mxFileInput', { static: false })
  private fileInput: ElementRef;

  constructor(
    private _dialogService: TdDialogService,
    private _maintenance: MaintenanceHttpClient,
    private _snackBar: MatSnackBar,
    private _store: Store<AppState>,
  ) {}

  public ngOnInit() {
    const flightLog$ = this._store.select(selectFlightLogById(this.flightLogId));

    this.flightLogNumber$ = flightLog$.pipe(map((fl) => fl?.flightSummaryId));

    this.flightLogStatus$ = flightLog$.pipe(
      map((fl) => fl?.status),
      filterNull(),
    );

    this.flightLogStatus$.pipe(takeUntil(this._onDestroy$)).subscribe((status) => {
      this.flightLogCompleted$.next(status === LogStatus.Complete);
    });

    this._maintenance
      .getFlightLogMaintenanceImageNameById(this.flightLogId)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((name) => {
        this.mxOperation.name$.next(
          truncate(name, {
            omission: '... [truncated]',
            length: this.mxOperation.maxNameLength,
          }),
        );
      });
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public async deleteMaintenanceImage() {
    if (this.mxOperation.running$.value || this.flightLogCompleted$.value) {
      return false;
    }
    const result = await this._dialogService
      .openConfirm({
        title: 'Delete FlightLog Image?',
        message: 'Are you sure you wish to delete this image?',
        acceptButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();
    if (result === true) {
      const flightLogNumber = await this.flightLogNumber$.pipe(take(1)).toPromise();
      try {
        this._setMxImageProgress('remove', true);
        const remove$ = this._maintenance
          .removeFlightLogMaintenanceImageById(this.flightLogId)
          .pipe(
            tap({
              next: (deleted) => {
                if (deleted && this.mxOperation.method !== 'cancel') {
                  this._showSnackBar(`Successfully deleted image for flight ${flightLogNumber}`);
                  this.mxOperation.name$.next(undefined);
                } else {
                  this._showSnackBar(`Unable to delete image for flight ${flightLogNumber}`);
                }
                this._resetMxImageProgress();
              },
              error: (err) => {
                this._showError(err, 'Error deleting image');
                this._resetMxImageProgress();
              },
            }),
          );
        this.mxOperation.sub = remove$.pipe(takeUntil(this._onDestroy$)).subscribe();
      } catch (err) {
        const error = `There was an error deleting the image for flight ${flightLogNumber}`;
        this._showError(err, error);
        this._resetMxImageProgress();
      }
    }
  }

  public async getMaintenanceImage() {
    if (this.mxOperation.running$.value) {
      return false;
    }
    this._setMxImageProgress('retrieve', true);
    try {
      const retrieve$ = this._maintenance.getFlightLogMaintenanceImageById(this.flightLogId).pipe(
        finalize(() => {
          this._resetMxImageProgress();
        }),
      );
      this.mxOperation.sub = retrieve$.pipe(takeUntil(this._onDestroy$)).subscribe({
        next: (res) => {
          if (this.mxOperation.method !== 'cancel') {
            const uri = window.URL.createObjectURL(res.body as Blob);
            window.open(uri?.toString(), '_blank');
            this._resetMxImageProgress();
          }
        },
        error: (err) => {
          this._showError(err, 'Error retrieving image');
          this._resetMxImageProgress();
        },
      });
    } catch (err) {
      const flightLogNumber = await this.flightLogNumber$.pipe(take(1)).toPromise();
      const error = `There was an error retrieving the image for flight ${flightLogNumber}.`;
      this._showError(err, error);
      this._resetMxImageProgress();
    }
  }

  public async uploadMaintenanceImage(e: Event) {
    if (this.mxOperation.running$.value) {
      return false;
    }
    const target = e.target as HTMLInputElement;
    const files = target.files as FileList;
    if (files.length > 0) {
      const file = files[0];
      const filename = file.name;
      let isValidFile = true;
      if (file?.type.toLowerCase().includes('image') === false) {
        isValidFile = false;
        await this._dialogService.openAlert({
          title: 'Invalid File Type',
          disableClose: true,
          message: `Only valid image files are allowed for upload.`,
          closeButton: 'Ok',
        });
      } else if (file?.size > maxMxImageFileSizeInBytes) {
        isValidFile = false;
        await this._dialogService.openAlert({
          title: 'File Too Big',
          disableClose: true,
          message: `Maximum upload image file size is ${this._convertBytesToMegabytes(
            maxMxImageFileSizeInBytes,
          )} MB.`,
          closeButton: 'Ok',
        });
      } else {
        isValidFile = await this._dialogService
          .openConfirm({
            title: 'Update FlightLog Image?',
            message: 'Are you sure you wish to upload this image?',
            acceptButton: 'Upload',
            cancelButton: 'Cancel',
          })
          .afterClosed()
          .toPromise();
      }
      if (isValidFile === true) {
        const flightLogNumber = await this.flightLogNumber$.pipe(take(1)).toPromise();
        this._setMxImageProgress('upload', true);
        const formData = new FormData();
        formData.append('imageFile', file, filename);
        try {
          const upload$ = this._maintenance.uploadFlightLogMaintenanceImageById(
            this.flightLogId,
            formData,
          );
          this.mxOperation.sub = upload$.pipe(takeUntil(this._onDestroy$)).subscribe({
            next: (ev) => {
              if (ev.type === HttpEventType.UploadProgress) {
                const totalBytes = ev.total ?? file.size;
                this.mxOperation.progress$.next(Math.round(100 * (ev.loaded / totalBytes)));
              }
            },
            complete: () => {
              if (this.mxOperation.method !== 'cancel') {
                this.mxOperation.name$.next(filename);
                this._showSnackBar(`Successfully updated image for flight ${flightLogNumber}.`);
              }
              this._resetMxImageProgress();
            },
            error: (err) => {
              this._showError(err, 'Error uploading image');
              this._resetMxImageProgress();
            },
          });
        } catch (err) {
          const error = `There was an error updating the image for flight ${flightLogNumber}.`;
          this._showError(err, error);
          this._resetMxImageProgress();
        }
      } else {
        this._resetMxImageProgress();
      }
    }
  }

  public async cancelMaintenanceImageProcess(e: Event) {
    if (!this.mxOperation.running$.value) {
      return false;
    }
    this._resetMxImageProgress('cancel');
  }

  public isMaintenceImageOperationInProgress$(method: string): Observable<boolean> {
    if (this.mxOperation == null) {
      return of(false);
    }
    switch (method) {
      case 'process':
      case 'remove':
      case 'retrieve':
      case 'upload':
        return this.mxOperation.running$.asObservable().pipe(
          map((running) => {
            return method === 'process' ? running : running && this.mxOperation.method === method;
          }),
        );
      default:
        return of(false);
    }
  }

  private _showError(err: any, defaultMsg?: string) {
    let msg = defaultMsg ?? 'Error';
    if ('error' in err) {
      const title = 'title' in err.error ? err.error.title : msg;
      const detail = 'detail' in err.error ? ` "${err.error.detail}"` : '';
      msg = `${title} ${detail}`;
    } else if ('status' in err && 'statusText' in err) {
      msg = `${msg} (${err.status} ${err.statusText})`;
    }
    this._showSnackBar(msg, 'ERROR', 8000);
  }

  private _resetMxImageProgress(method?: OperationMethod) {
    this.mxOperation.sub.unsubscribe();
    this._setMxImageProgress(method);
    this.fileInput.nativeElement.value = '';
  }

  private _setMxImageProgress(
    method: OperationMethod = 'none',
    running: boolean = false,
    progress: number = 0,
  ) {
    this.mxOperation.method = method;
    this.mxOperation.running$.next(running);
    this.mxOperation.progress$.next(progress);
  }

  private _showSnackBar(message: string, action?: string | undefined, milliseconds: number = 3000) {
    this._snackBar.open(message, action, {
      duration: milliseconds,
    });
  }

  private _convertBytesToMegabytes(bytes: number) {
    return bytes / 1024 / 1024;
  }
}
